<template>
  <Modal
    name="CREATE_UPDATE_HAUSEFUL_USER_MODAL"
    ref="modalCreateUpdateHausefulUsers"
    :title="title"
    :width="600"
    actions
  >
    <template v-slot:body>
      <h-loading v-if="isHausefulUserLoading" />

      <v-form v-else v-model="valid" ref="hausefulUsersForm" lazy-validation>
        <v-tabs v-if="isInspector" v-model="currentTab" color="realEstate">
          <v-tab>
            {{ labels.information }}
          </v-tab>
          <v-tab>
            {{ labels.activeServices }}
          </v-tab>
        </v-tabs>

        <v-card elevation="0" :class="isInspector ? 'px-5 py-3' : 'pa-0'" :outlined="isInspector">
          <v-tabs-items v-model="currentTab">
            <v-tab-item>
              <v-row v-if="isNotAUpdate">
                <v-col cols="12" sm="9">
                  <v-select
                    v-model="userType"
                    :label="$t('hausefulUsers.label.userType')"
                    :items="hausefulUsersTypes"
                    :rules=[rules.required]
                    item-value="value"
                    item-text="name"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="isTester"
                    :label="$t('hausefulUsers.label.isUserTester')"
                    :items="userStatus"
                    item-value="value"
                    item-text="label"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-row v-show="isRealEstateOrOperator">
                <v-col>
                  <v-autocomplete
                    v-model="realEstateId"
                    :search-input.sync="realEstateSearch"
                    :no-data-text="$t('emptyState.noDataDoTheSearch')"
                    :label="$t('hausefulUsers.label.realEstate')"
                    :items="realEstateList"
                    :loading="isRealEstateLoading"
                    :disabled="!isNotAUpdate"
                    prepend-inner-icon="mdi-magnify"
                    hide-details="auto"
                    item-text="name"
                    item-value="id"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-row v-show="isRealEstateOrOperator && realEstateId">
                <v-col>
                  <AdminBranchSelect
                    v-show="isRealEstateOrOperator && realEstateId"
                    v-model="branch"
                    :realEstateId="realEstateId"
                    :label="$t('hausefulUsers.label.branch')"
                    :key="realEstateId"
                    :dense="false"
                    :showOuterLabel="false"
                    prepend-inner-icon="mdi-magnify"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" :sm="isNotAUpdate ? '12' : '9'" class="pb-0">
                  <v-text-field
                    v-model="name"
                    :label="$t('hausefulUsers.label.name')"
                    :rules=[rules.name]
                    outlined
                  />
                </v-col>
                <v-col v-if="!isNotAUpdate" class="pb-0" cols="12" sm="3">
                  <v-select
                    v-model="isTester"
                    :label="$t('hausefulUsers.label.isUserTester')"
                    :items="userStatus"
                    item-value="value"
                    item-text="label"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-text-field
                v-model="username"
                :label="$t('hausefulUsers.label.email')"
                :rules=[rules.email]
                autocomplete="username"
                outlined
              />

              <HPhone
                v-model="phone"
                :default-value="phone"
                :rules=[rules.phone]
                :country="country"
                class="mb-n3"
              />

              <v-row>
                <v-col class="pb-0" cols="12" sm="5">
                  <HDocumentID
                    v-model="cpf"
                    :country="country"
                    :default-value="hausefulUser.cpf"
                    :label="$t('forms.cpf')"
                    :rules="[rules.documentCheck]"
                  />
                </v-col>
                <v-col class="pb-0" cols="12" sm="4">
                  <v-text-field
                    v-model="rg"
                    :maxlength="10"
                    :label="$t('hausefulUsers.label.rg')"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col class="pb-0" cols="12" sm="3">
                  <v-text-field
                    v-model="birthDate"
                    v-mask="'##/##/####'"
                    :label="$t('hausefulUsers.label.birthDate')"
                    :rules=[rules.birthDateCheckNotRequired]
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-autocomplete
                v-show="isPartner"
                v-model="partnerId"
                :search-input.sync="partnersSearch"
                :no-data-text="$t('emptyState.noDataDoTheSearch')"
                :label="$t('host.label.partner')"
                :items="partners"
                prepend-inner-icon="mdi-magnify"
                hide-details="auto"
                item-text="name"
                item-value="id"
                class="mb-8"
                outlined
              />

              <!-- location -->
              <h4>
                {{ $t('hausefulUsers.label.location') }}
              </h4>

              <v-row>
                <v-col class="pb-0" cols="12" sm="6">
                  <HLanguageSelect
                    v-model="language"
                    :default-value="language"
                    :country="country"
                  />
                </v-col>
                <v-col class="pb-0" cols="12" sm="6">
                  <HCurrencySelect
                    v-model="currency"
                    :default-value="currency"
                    :country="country"
                  />
                </v-col>
              </v-row>

              <v-autocomplete
                v-model="cityId"
                :label="$t('hausefulUsers.label.city')"
                :search-input.sync="citiesSearch"
                :loading="isCitiesLoading"
                :rules=[rules.required]
                :items="cities"
                prepend-inner-icon="mdi-magnify"
                item-text="name"
                item-value="id"
                class="mb-n4"
                hide-no-data
                outlined
              >
                <template v-slot:selection="{ item }">
                  {{ formatCityState(item) }}
                </template>
                <template v-slot:item="{ item }">
                  {{ formatCityState(item) }}
                </template>
              </v-autocomplete>

              <!-- password -->
              <h4 class="mt-5">
                {{ $t('hausefulUsers.label.password') }}
              </h4>

              <v-row>
                <v-col>
                  <v-text-field
                    v-model="password"
                    @input="passwordCheck"
                    @click:append="showPass = !showPass"
                    :append-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                    :type="showPass ? 'text' : 'password'"
                    :label="$t('hausefulUsers.label.password')"
                    :rules="isPasswordRequired ? [rules.password] : []"
                    autocomplete="new-password"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="repeatPassword"
                    @input="passwordCheck"
                    :label="$t('hausefulUsers.label.repeatPassword')"
                    :type="showPass ? 'text' : 'password'"
                    :rules="isPasswordRequired ? [rules.password] : []"
                    :error-messages="!passwordMatches ? errorMessage : ''"
                    :error="!passwordMatches"
                    autocomplete="new-password"
                    hide-details="auto"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <ServiceTypesSelection
                v-if="!isHausefulUserLoading"
                @update:service-types="updateServiceTypes"
                :real-estate-id="realEstateId"
                :current-service-types="hausefulUser.service_types"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-form>
    </template>
    <template v-slot:actions>
      <div class="d-flex">
        <v-btn
          @click="closeAllModals"
          :disabled="isHausefulUserSaving"
          class="grey--text mr-5"
          elevation="0"
          large
          text
        >
          {{ $t('hausefulUsers.action.cancel') }}
        </v-btn>

        <v-btn
          @click="save"
          :loading="isHausefulUserSaving"
          color="primary"
          elevation="0"
          large
        >
          {{ $t('hausefulUsers.action.save') }}
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

import { hausefulUsersTypes } from '@/util/enums'

// utils
import { unmaskText, maskDate, formatBirthDate } from '@/util/helpers'
import {
  name,
  email,
  phone,
  required,
  password,
  documentCheck,
  birthDateCheckNotRequired,
} from '@/util/errorHandling'

// components
import Modal from '@/components/app/Modal'
import HPhone from '@/components/app/HPhone'
import HDocumentID from '@/components/app/HDocumentID'
import AdminBranchSelect from '../../../components/branch/AdminBranchSelect.vue'
import HLanguageSelect from '../../../components/app/HLanguageSelect.vue'
import HCurrencySelect from '../../../components/app/HCurrencySelect.vue'
import ServiceTypesSelection from '../fragments/ServiceTypesSelection.vue'

export default {
  name: 'HausefulUserCreateUpdate',

  components: {
    Modal,
    HDocumentID,
    HPhone,
    AdminBranchSelect,
    HLanguageSelect,
    HCurrencySelect,
    ServiceTypesSelection,
  },

  computed: {
    ...mapGetters([
      'user',
      'cities',
      'hausefulUser',
      'isCitiesLoading',
      'isHausefulUserSaving',
      'isHausefulUserLoading',
      'partners',
      'realEstateList',
      'branches',
    ]),

    isNotAUpdate() {
      return !this.hausefulUser?.id
    },

    isPasswordRequired() {
      return this.isNotAUpdate && this.password === null
    },

    errorMessage() {
      return this.$t('onboarding.register.errors.passwordMismatch')
    },

    title() {
      return this.isNotAUpdate ? this.createTitle : this.updateTitle
    },

    country() {
      return this.user?.country ?? localStorage.getItem('user_country')
    },

    isRealEstateOrOperator() {
      return ['ROLE_REAL_ESTATE', 'ROLE_REAL_ESTATE_OPERATOR'].includes(this.userType)
    },

    isInspector() {
      return ['ROLE_INSPECTOR'].includes(this.userType)
    },

    isPartner() {
      return this.userType === 'ROLE_PARTNER'
    },
  },

  methods: {
    ...mapMutations([
      'setCities',
      'closeAllModals',
      'setRealEstateList',
    ]),

    ...mapActions([
      'searchCity',
      'fetchPartners',
      'saveHausefulUser',
      'fetchRealEstatesBySearchText',
    ]),

    updateServiceTypes(serviceTypes) {
      this.selectedServiceTypes = serviceTypes
    },

    save() {
      if (!this.$refs.hausefulUsersForm.validate()) return

      const body = {
        id: this.hausefulUser?.id ? this.hausefulUser.id : null,
        name: this.name,
        username: this.username,
        rg: unmaskText(this.rg),
        cpf: unmaskText(this.cpf),
        phone: this.phone,
        password: this.password,
        user_type: this.userType,
        is_tester: this.isTester,
        birth_date: formatBirthDate(this.birthDate),
        city_id: this.cityId,
        partner_id: this.partnerId,
        real_estate_id: this.realEstateId,
        branch_id: this.branch ? this.branch.id : null,
        language: this.language,
        currency: this.currency,
        service_types: this.selectedServiceTypes,
      }

      this.saveHausefulUser(body)
    },

    passwordCheck() {
      if (!this.repeatPassword) return

      this.password == this.repeatPassword
        ? this.passwordMatches = true
        : this.passwordMatches = false
    },

    formatCityState(city) {
      const cityName = city?.name ? city.name : '---'
      const stateName = city?.state?.acronym ? city.state.acronym : '---'
      return `${cityName} / ${stateName}`
    },

    fillFields() {
      this.currentTab = 0
      this.setCities([this.hausefulUser.city])
      this.setRealEstateList([this.hausefulUser.real_estate])

      this.rg = this.hausefulUser.rg
      this.cpf = this.hausefulUser.cpf
      this.name = this.hausefulUser.name
      this.username = this.hausefulUser.username
      this.phone = this.hausefulUser.phone
      this.birthDate = maskDate(this.hausefulUser.birth_date, 'br')
      this.userType = this.hausefulUser.user_type
      this.isTester = this.hausefulUser.is_tester
      this.cityId = this.hausefulUser.city.id
      this.partnerId = this.hausefulUser.partner ? this.hausefulUser.partner.id : null
      this.realEstateId = this.hausefulUser.real_estate ? this.hausefulUser.real_estate.id : null
      this.branch = this.hausefulUser.branch
      this.language = this.hausefulUser.language
      this.currency = this.hausefulUser.currency
      this.password = null
      this.repeatPassword = null
      this.selectedServiceTypes = this.hausefulUser.service_types ? [...this.hausefulUser.service_types] : []
    },

    resetFields() {
      this.setCities([])

      this.rg = null
      this.cpf = null
      this.name = null
      this.username = null
      this.phone = null
      this.birthDate = null
      this.userType = null
      this.isTester = false
      this.cityId = null
      this.password = null
      this.repeatPassword = null
      this.partnerId = null
      this.realEstateId = null
      this.branch = null
      this.language = 'PT_BR'
      this.currency = 'BRAZILIAN_REAL'
      this.selectedServiceTypes = []
    },

    // checkModalAndFetchData() {
    //   const modalIsOpen = this.$refs.modalCreateUpdateHausefulUsers?.dialog
    //   if (!modalIsOpen) return

    //   this.fetchPartners()
    //   this.realEstateSearch()
    // },
  },

  watch:{
    citiesSearch(term) {
      if (!term || term.length <= 3) return

      clearTimeout(this._timer)

      this._timer = setTimeout(() => {
        this.searchCity(term)
      }, 1000)
    },

    partnersSearch(term) {
      if (!term || term.length <= 3) return

      clearTimeout(this._timer)

      this._timer = setTimeout(() => {
        this.fetchPartners(term)
      }, 1000)
    },

    realEstateSearch(term) {
      if (!term || term.length <= 3) return

      this.isRealEstateLoading = true
      clearTimeout(this._timer)

      this._timer = setTimeout(async () => {
        await this.fetchRealEstatesBySearchText(term)
        this.isRealEstateLoading = false
      }, 1000)
    },

    hausefulUser(user) {
      if (!user.id) return
      this.fillFields()
    },
  },

  data() {
    return {
      createTitle: this.$t('hausefulUsers.action.create'),
      updateTitle: this.$t('hausefulUsers.action.edit'),

      valid: false,
      showPass: false,
      citiesSearch: null,
      partnersSearch: null,
      passwordMatches: true,
      isRealEstateLoading: false,
      _timer: null,

      rg: null,
      cpf: null,
      name: null,
      username: null,
      password: null,
      repeatPassword: null,
      phone: null,
      birthDate: null,
      userType: null,
      isTester: false,
      cityId: null,
      partnerId: null,
      realEstateId: null,
      branch: null,
      language: null,
      currency: null,
      realEstateSearch: null,

      hausefulUsersTypes,
      userStatus: [
        { value: true,  label: this.$t('hausefulUsers.label.yes') },
        { value: false,  label: this.$t('hausefulUsers.label.no') },
      ],

      rules: {
        name,
        email,
        phone,
        required,
        password,
        documentCheck,
        birthDateCheckNotRequired,
      },

      currentTab: 0,
      selectedServiceTypes: [],

      labels: {
        activeServices: this.$t('hausefulUsers.label.activeServices'),
        information: this.$t('hausefulUsers.label.information'),
        location: this.$t('hausefulUsers.label.location'),
      }
    }
  },
}
</script>
